import { useCallback, useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import _ from 'lodash'
import { useActiveState } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import useGroupList from './useGroupList'

const useEditableGroupList = (props) => {
  const dispatch = useDispatch()
  const { product, subCategory } = useRouteContext('useEditableGroupList')
  const { groups, setGroups, other, setOther } = useGroupList(props)

  const { active: activeGroupId, setActive: setActiveGroupId } = useActiveState(
    product ? `product-${product.id}` : `category-${subCategory.id}`
  )
  useLayoutEffect(() => {
    if (
      groups?.length &&
      (!activeGroupId || !groups.find(({ id }) => id === activeGroupId))
    ) {
      setActiveGroupId(groups[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups])

  const onGroupMove = useCallback(
    (srcIndex, destIndex) => {
      const srcId = groups?.[srcIndex]
      const destId = groups?.[destIndex]
      if (destId === activeGroupId) {
        setActiveGroupId(srcId)
      } else if (srcId === activeGroupId) {
        setActiveGroupId(destId)
      }
      setGroups((groups) => {
        const newGroups = [...groups]
        newGroups.splice(srcIndex, 1)
        newGroups.splice(destIndex, 0, groups[srcIndex])
        return newGroups
      })
    },
    [activeGroupId, setActiveGroupId, groups, setGroups]
  )

  const onGroupDrop = useCallback(() => {
    dispatch(
      bshSlice.reorderPropertiesGroups({
        data: {
          owner: product ?? subCategory,
          ids: groups.map(({ id }) => id),
        },
      })
    )
  }, [dispatch, groups, product, subCategory])

  const onPropertyMove = useCallback(
    (srcId, destId, fallbackGroupIndex) => {
      let sourceGroupIndex = -1
      let sourceIndex = -1
      let destGroupIndex = -1
      let destIndex = -1
      const findIndexes = (properties, groupIndex) => {
        let pos = properties.indexOf(srcId)
        if (pos >= 0) {
          sourceIndex = pos
          sourceGroupIndex = groupIndex
        }
        pos = properties.indexOf(destId)
        if (pos >= 0) {
          destIndex = pos
          destGroupIndex = groupIndex
        }
      }
      _.each(groups, (group, i) => {
        findIndexes(group.properties, i)
      })
      findIndexes(other, groups.length)

      if (!destId && fallbackGroupIndex !== undefined) {
        destGroupIndex = fallbackGroupIndex
        if (destGroupIndex < groups.length) {
          destIndex = groups[destGroupIndex].properties.length
        } else {
          // destIndex = other.length
          destIndex = 0
        }
      }

      // If we're not actually moving anything, don't bother resetting state
      if (sourceGroupIndex === destGroupIndex && sourceIndex === destIndex) {
        return
      }

      const newGroups = _.cloneDeep(groups)
      const newOther = _.cloneDeep(other)

      if (sourceGroupIndex !== -1 && sourceIndex !== -1) {
        // Remove the item from its current spot
        if (sourceGroupIndex < newGroups.length) {
          newGroups[sourceGroupIndex].properties.splice(sourceIndex, 1)
        } else {
          newOther.splice(sourceIndex, 1)
        }
      }
      if (destGroupIndex !== -1 && destIndex !== -1) {
        // Add it to its new spot
        if (destGroupIndex < newGroups.length) {
          newGroups[destGroupIndex].properties.splice(destIndex, 0, srcId)
        } else {
          newOther.splice(destIndex, 0, srcId)
        }
      }

      setGroups(newGroups)
      setOther(newOther)

      return { groups: newGroups, other: newOther }
    },
    [groups, other, setGroups, setOther]
  )

  const onPropertyDrop = useCallback(
    (params) => {
      const g = params?.groups ?? groups
      const o = params?.other ?? other
      const data = {
        groups: g.map((group) => ({
          id: group.id,
          properties: [...group.properties],
        })),
        other: [...o],
      }
      dispatch(
        bshSlice.reorderProperties({
          data: {
            owner: product ?? subCategory,
            data,
          },
        })
      )
    },
    [dispatch, groups, other, product, subCategory]
  )

  const saveProperty = useCallback(
    (property) => {
      dispatch(
        bshSlice.storeProperty({
          data: {
            owner: product ?? subCategory,
            property,
          },
        })
      )
    },
    [dispatch, product, subCategory]
  )

  const onAttachProperty = useCallback(
    (property) => {
      const moved = onPropertyMove(
        property.id,
        null,
        groups?.findIndex(({ id }) => id === activeGroupId)
      )
      onPropertyDrop(moved)
    },
    [activeGroupId, groups, onPropertyDrop, onPropertyMove]
  )

  const onDetachProperty = useCallback(
    (property) => {
      const fallbackGroupIndex = /M/.test(property.status)
        ? undefined
        : groups.length
      const moved = onPropertyMove(property.id, null, fallbackGroupIndex)
      onPropertyDrop(moved)
    },
    [groups.length, onPropertyDrop, onPropertyMove]
  )

  const onPropertyToggleSkip = useCallback(
    (data) => dispatch(bshSlice.togglePropertySkip({ data })),
    [dispatch]
  )

  return useMemo(
    () => ({
      groups,
      other,
      activeGroupId,
      setActiveGroupId,
      onAttachProperty,
      onDetachProperty,
      onGroupMove,
      onGroupDrop,
      onPropertyMove,
      onPropertyDrop,
      onPropertyToggleSkip,
      saveProperty,
    }),
    [
      activeGroupId,
      groups,
      onAttachProperty,
      onDetachProperty,
      onGroupDrop,
      onGroupMove,
      onPropertyDrop,
      onPropertyMove,
      onPropertyToggleSkip,
      other,
      saveProperty,
      setActiveGroupId,
    ]
  )
}

export default useEditableGroupList
